import React, { useCallback, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Forms from 'components/Forms';
import {
  ExistingPolicyType,
  FundAllocationType
} from 'store/modules/ExistingPolicies/types';
import { MetaType } from 'store/modules/Meta/types';
import { Button, Loader, Modal } from 'components/Base';
import Router from 'components/Router/Router';
import { errHelper, formatCurrency, formatPercent } from 'utils';
import BottomNavBar from 'components/Cases/BottomNavBar';
import Icons from 'components/Icons';
import selectors from 'store/selectors';
import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import { CustomerType } from 'store/modules/Customers/types';
import DecisionModal from './DecisionModal';
import IoImportPolicies from './IoImportPolicies';
import IoIncompleteAndSavedPolicies from './IoIncompleteAndSavedPolicies';
import createPolicyArray from './utils';
import actions from 'store/actions';
import api from 'api';
import { AxiosError } from 'axios';
import { initialiseOverrides } from 'components/Policies/utils';

const { useHistory, useParams } = Router;

interface ExistingPoliciesProps {
  existingPolicies: Array<ExistingPolicyType>;
  chargeTypes: Array<MetaType>;
  addNewPolicy: any;
  removeExistingPolicy: any;
  markAsSaved: any;
  customers: CustomerType[];
  setExistingPolicies: Function;
  setIsImportingPolicies: Function;
  isImportingPolicies: boolean;
}

export const prepareFunds = (wrapAmmount: string, fundAllocations: any) => {
  const fundsCopy = [...fundAllocations];

  if (wrapAmmount) {
    fundsCopy.push({
      fund: {
        name: 'Wrap Cash',
        active: true
      },
      fund_value: wrapAmmount
    });
  }

  return fundsCopy.map((fund: any) => {
    return {
      ...fund,
      fund_charge_overrides: initialiseOverrides(fund),
      fund_value: formatCurrency(fund.fund_value),
      fund: fund.fund
    };
  });
};

interface FundChargeType {
  kind: number;
  cash_value: string;
  percentage_value: string;
  source_value: 1 | 2;
  manually_overriden: boolean;
}
export const prepareMpCharges = (fundsAllocation: FundAllocationType[]) => {
  const charges: any = [];

  fundsAllocation.forEach((fund: FundAllocationType) => {
    // SUPPORT ONLY FOR MP CHARGES
    if (!fund.model_portfolio) return;

    if (fund.charges && fund.charges.length > 0) {
      fund.charges.forEach((charge: FundChargeType) => {
        if (charge.kind === 8) {
          charges.push({
            name: fund.model_portfolio
              ? fund.model_portfolio.name
              : fund.fund.name,
            cash_value: formatCurrency(charge.cash_value),
            percentage_value: formatPercent(
              Number(charge.percentage_value) * 100,
              true
            ),
            id: fund.model_portfolio ? fund.model_portfolio.id : fund.fund.id,
            source_value: charge.source_value,
            manually_overriden: charge.manually_overriden
          });
        }
      });
    } else {
      charges.push({
        name: fund.model_portfolio ? fund.model_portfolio.name : fund.fund.name,
        cash_value: formatCurrency(0),
        percentage_value: formatPercent(0),
        id: fund.model_portfolio ? fund.model_portfolio.id : fund.fund.id,
        source_value: null
      });
    }
  });

  return charges;
};

type DecisionModal = {
  isVisible: boolean;
  type: 'expanded' | 'short';
};

const ManagePolicies = ({
  existingPolicies,
  chargeTypes,
  addNewPolicy,
  removeExistingPolicy,
  markAsSaved,
  customers,
  setExistingPolicies,
  setIsImportingPolicies,
  isImportingPolicies
}: ExistingPoliciesProps) => {
  const [openIoImport, setOpenIoImport] = useState(false);
  const areAnyClientsFromInteliflo = customers.find(
    c => c.intelligent_office_id !== null
  ); // CHECK IF ANY CUSTOMER IS FROM IO
  const [shouldDisplayDecisionModal, setDisplayDecisionModal] =
    useState<DecisionModal>({
      isVisible: false,
      type: 'expanded'
    });

  const [incompletePolicies, setIncompletePolices] = useState([]);
  const [completePolicies, setCompletePolices] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const array = createPolicyArray(existingPolicies, customers, chargeTypes);
  const history = useHistory();
  const { portfolio } = useParams() as any;

  const lastPolicy = [...array].pop();
  const isLastPolicySaved = !!(lastPolicy && lastPolicy.id);
  const isAnyPolicyExisit = array.length > 0;
  const shouldDisplauFirstDecissionQuestion =
    shouldDisplayDecisionModal.isVisible &&
    shouldDisplayDecisionModal.type === 'expanded';

  const shouldDisplauDecissionQuestion =
    shouldDisplayDecisionModal.isVisible &&
    shouldDisplayDecisionModal.type === 'short';

  const moveToComplete = (policy: any) => {
    const newIncompletePolicies = incompletePolicies.filter(
      (p: any) => p.io_id !== policy.io_id
    );
    const newCompletePolicies = [...completePolicies, policy.policy];
    setIncompletePolices(newIncompletePolicies);
    setCompletePolices(newCompletePolicies as any);
  };

  const removeIncompletePolicy = (policy: any) => {
    const newIncompletePolicies = incompletePolicies.filter(
      (p: any) => p.io_id !== policy.io_id
    );
    setIncompletePolices(newIncompletePolicies);
    if (newIncompletePolicies.length === 0 && completePolicies.length === 0)
      setIsImportingPolicies(false);
  };
  const removeCompletePolicy = (policy: any) => {
    const newCompletePolicies = completePolicies.filter(
      (p: any) => p.id !== policy.id
    );
    setCompletePolices(newCompletePolicies);
    if (newCompletePolicies.length === 0 && incompletePolicies.length === 0)
      setIsImportingPolicies(false);
  };

  const triggerAddPolicy = () => {
    areAnyClientsFromInteliflo
      ? setDisplayDecisionModal({
          isVisible: true,
          type: 'short'
        })
      : addNewPolicy({ id: null });
  };

  const updatePolicies = useCallback(() => {
    if (
      incompletePolicies.length === 0 &&
      completePolicies.length !== 0 &&
      !isImportingPolicies
    ) {
      setDisplayDecisionModal({ isVisible: false, type: 'expanded' });
      setIsImportingPolicies(false);
      setIsLoading(true);
      api
        .getExistingPolicies({ portfolio })
        .then(({ data }: any) => {
          setExistingPolicies(data);
          setDisplayDecisionModal({ isVisible: false, type: 'short' });
          setIsLoading(false);
        })
        .catch((err: AxiosError) => {
          errHelper(err);
          setIsLoading(false);
        });
    }
  }, [
    setExistingPolicies,
    setDisplayDecisionModal,
    setIsImportingPolicies,
    incompletePolicies,
    completePolicies,
    portfolio,
    isImportingPolicies
  ]);

  useEffect(() => {
    updatePolicies();
  }, [incompletePolicies, completePolicies, updatePolicies]);

  useEffect(() => {
    setDisplayDecisionModal({
      isVisible:
        areAnyClientsFromInteliflo &&
        existingPolicies &&
        existingPolicies.length < 1
          ? true
          : false,
      type: 'expanded'
    });
  }, [existingPolicies, areAnyClientsFromInteliflo, customers]);

  return (
    <Loader isLoading={isLoading}>
      <div style={{ paddingBottom: '86px' }}>
        {incompletePolicies.length === 0 && !isImportingPolicies ? (
          shouldDisplauFirstDecissionQuestion ? (
            <DecisionModal
              setOpenIoImport={() => {
                setIsImportingPolicies(true);
                setOpenIoImport(true);
                setDisplayDecisionModal({ isVisible: false, type: 'expanded' });
              }}
              type={shouldDisplayDecisionModal.type}
              continueWithManual={() => {
                addNewPolicy({ id: null });
                setDisplayDecisionModal({ isVisible: false, type: 'expanded' });
              }}
            />
          ) : (
            <Grid container>
              <Modal
                isOpen={shouldDisplauDecissionQuestion}
                closeModalFunc={() =>
                  setDisplayDecisionModal({ isVisible: false, type: 'short' })
                }
              >
                <DecisionModal
                  type={shouldDisplayDecisionModal.type}
                  closeModal={() =>
                    setDisplayDecisionModal({ isVisible: false, type: 'short' })
                  }
                  setOpenIoImport={() => {
                    setIsImportingPolicies(true);
                    setOpenIoImport(true);
                    setDisplayDecisionModal({
                      isVisible: false,
                      type: 'expanded'
                    });
                  }}
                  continueWithManual={() => {
                    addNewPolicy({ id: null });
                    setDisplayDecisionModal({
                      isVisible: false,
                      type: 'short'
                    });
                  }}
                />
              </Modal>
              <Grid item xs={12}>
                {array.map((policy: any, index: number) => {
                  return (
                    <Forms.ManagePolicies.Form
                      form={`policy-${index}`}
                      key={`policy-${index}`}
                      initialValues={policy}
                      policyIndex={index}
                      removeLocalPolicy={removeExistingPolicy}
                      markAsSaved={markAsSaved}
                    />
                  );
                })}
              </Grid>
              <Button
                key="add new policy button"
                variant="contained"
                color="secondary"
                disabled={!isLastPolicySaved && isAnyPolicyExisit}
                id="add-policy"
                onClick={() => triggerAddPolicy()}
              >
                {'ADD POLICY'}
              </Button>
              <BottomNavBar
                rightButtons={[
                  <React.Fragment
                    key={
                      !isAnyPolicyExisit
                        ? 'ADD EXISTING POLICY BUTTON'
                        : 'ADD ANOTHER POLICY BUTTON'
                    }
                  >
                    {isAnyPolicyExisit && (
                      <Button
                        endIcon={<Icons.RightArrow />}
                        disabled={!isAnyPolicyExisit}
                        onClick={() =>
                          history.push(
                            `/${portfolio}/existing-policies/policy-analysis`
                          )
                        }
                      >
                        Continue to analysis
                      </Button>
                    )}
                  </React.Fragment>,
                  <React.Fragment key="button fragment">
                    {!isAnyPolicyExisit && (
                      <Button
                        endIcon={<Icons.RightArrow />}
                        key="Skip for now"
                        onClick={() =>
                          history.push(`/${portfolio}/recommendations`)
                        }
                      >
                        Skip for now
                      </Button>
                    )}
                  </React.Fragment>
                ]}
              />
            </Grid>
          )
        ) : (
          <IoIncompleteAndSavedPolicies
            removeIncompletePolicy={removeIncompletePolicy}
            removeCompletePolicy={removeCompletePolicy}
            moveToComplete={moveToComplete}
            incompletePolicies={createPolicyArray(
              incompletePolicies,
              customers,
              chargeTypes
            )}
            completePolicies={createPolicyArray(
              completePolicies,
              customers,
              chargeTypes
            )}
          />
        )}

        {customers &&
          !!customers.find(customer => !!customer.intelligent_office_id) &&
          isImportingPolicies && (
            <IoImportPolicies
              setExistingPolicies={setExistingPolicies}
              isModalOpen={openIoImport}
              closeModal={() => setOpenIoImport(false)}
              customers={customers}
              setIncompletePolices={setIncompletePolices}
              setCompletePolicies={setCompletePolices}
              isImportingPolicies={isImportingPolicies}
            />
          )}
      </div>
    </Loader>
  );
};

export default compose(
  connect(
    (state: any) => ({
      customers: selectors.customersSelectors.customers(state),
      isImportingPolicies:
        selectors.importingPoliciesSelectors.isImportingPolicies(state)
    }),
    {
      setExistingPolicies: actions.caseActions.setExistingPolicies,
      setIsImportingPolicies: actions.PolicyImportActions.setIsImportingPolicies
    }
  )
)(ManagePolicies);
